import type { WorldState } from "../models/worldState.ts";
import {
  activeCount,
  canSelectNext,
  canSelectPrevious,
  currentMessage,
  currentNotification,
  formatNotificationPageOf,
  resolveNotificationNavIndicator,
  shouldShowNavRow,
  type LocalizedKindResolver,
} from "../reducers/notifications/notificationStore.ts";

/** Chrome copy + kind resolver for `selectNotificationView`. Supplied by `@dawn/i18n`. */
export interface NotificationViewCopy {
  readonly hudNotificationsTooltip: string;
  readonly notificationEmpty: string;
  readonly notificationOk: string;
  readonly notificationCancel: string;
  readonly notificationMarkDone: string;
  readonly notificationPageOf: string;
  readonly notificationPageIndicatorOf: string;
  readonly resolveKind: LocalizedKindResolver;
}

export interface NotificationViewModel {
  readonly badgeCount: number;
  readonly bellTooltip: string;
  readonly isOpen: boolean;
  readonly isEmpty: boolean;
  readonly emptyText: string;
  readonly title: string;
  readonly body: string;
  readonly showNavRow: boolean;
  readonly navIndicatorText: string;
  readonly previousEnabled: boolean;
  readonly nextEnabled: boolean;
  readonly showMarkDone: boolean;
  readonly showDecisionRow: boolean;
  readonly okLabel: string;
  readonly cancelLabel: string;
  readonly markDoneLabel: string;
  readonly currentId: string | null;
}

export function selectNotificationView(
  state: WorldState,
  copy: NotificationViewCopy,
): NotificationViewModel {
  const notifications = state.notifications;
  const count = activeCount(notifications);
  const current = currentNotification(notifications);
  const showNavRow = shouldShowNavRow(notifications);
  const requiresDecision = current?.requiresDecision ?? false;
  const navIndicator = resolveNotificationNavIndicator(notifications);

  if (count === 0) {
    return {
      badgeCount: 0,
      bellTooltip: copy.hudNotificationsTooltip,
      isOpen: false,
      isEmpty: true,
      emptyText: copy.notificationEmpty,
      title: "",
      body: "",
      showNavRow: false,
      navIndicatorText: "",
      previousEnabled: false,
      nextEnabled: false,
      showMarkDone: false,
      showDecisionRow: false,
      okLabel: copy.notificationOk,
      cancelLabel: copy.notificationCancel,
      markDoneLabel: copy.notificationMarkDone,
      currentId: null,
    };
  }

  const message = currentMessage(notifications, copy.resolveKind);
  const title = message?.title ?? "";
  const body = message?.body ?? "";

  const navIndicatorText = navIndicator
    ? formatNotificationPageOf(
        navIndicator.isPage ? copy.notificationPageIndicatorOf : copy.notificationPageOf,
        navIndicator.current - 1,
        navIndicator.total,
      )
    : "";

  return {
    badgeCount: count,
    bellTooltip: copy.hudNotificationsTooltip,
    isOpen: true,
    isEmpty: false,
    emptyText: copy.notificationEmpty,
    title,
    body,
    showNavRow,
    navIndicatorText,
    previousEnabled: canSelectPrevious(notifications),
    nextEnabled: canSelectNext(notifications),
    showMarkDone: !requiresDecision,
    showDecisionRow: requiresDecision,
    okLabel: copy.notificationOk,
    cancelLabel: copy.notificationCancel,
    markDoneLabel: copy.notificationMarkDone,
    currentId: current?.id ?? null,
  };
}
